import './styles.scss'

import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router'

import B2BecOrderDetailTitle from '@/components/B2BecOrderDetailTitle'
import CustomButton from '@/components/CustomButton'
import { ChevronLeftIcon } from '@/components/Icons'
import { useAppSelector } from '@/store/hook'
import { selectHideStatus, selectShowVAT } from '@/store/selectors/configSelector'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import usePermission from '@/hooks/usePermission'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { MODULE_ORDER_RIGHTS } from '@/libs/constants/modulerights'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'

import DownloadButton from '../components/DownloadButton'
import OrderDetailsTable from './OrderDetailsTable'
import { useOrderQuery } from './useOrderQuery'

const OrderDetails = ({ breadcrumbs }) => {
  const { orderNumber } = useParams()
  const location = useLocation()
  const currentPage = location.state?.currentPage || '1'

  const { hasPermission: hasDownloadInvoicePermission } = usePermission(
    MODULE_ORDER_RIGHTS.DOWNLOAD_INVOICE,
  )

  useDocumentTitle('pageTitle.order $1', [orderNumber])
  useBreadcrumbs(breadcrumbs, [
    { titleData: [orderNumber], pathData: [orderNumber] },
  ])

  const { setPageInfoData } = useAdobeAnalysis()

  const navigate = useNavigate()
  const { t } = useTranslation()

  const { isLoadingOrder, data } = useOrderQuery({ orderNumber })
  const hideStatusColumnEnabled = useAppSelector(selectHideStatus)
  const showVATEnabled = useAppSelector(selectShowVAT)

  useEffect(() => {
    setPageInfoData({
      pageName: `order ${orderNumber}`,
      pageId: '6',
      pageType: PAGE_TYPES.ORDER_DETAILS,
    })
  }, [setPageInfoData, orderNumber])

  const isDownloadButtonDisabled = isLoadingOrder
    || !hasDownloadInvoicePermission
    || !isValidArray(data?.invoicesNumbers)

  const handleOnRow = useCallback(
    ({ materialNumber, isAvailable }) => {
      return {
        onClick: () => {
          if (!isLoadingOrder && isAvailable && materialNumber !== '') {
            navigate(linkGenerator(`/product/${materialNumber}`))
            return
          }

          return null
        },
      }
    },
    [isLoadingOrder, navigate],
  )

  return (
    <div className="order-detail__wrapper">
      <B2BecOrderDetailTitle
        hideStatusColumn={hideStatusColumnEnabled}
        hideVATFields={!showVATEnabled}
        showSummary
        isLoading={isLoadingOrder}
        orderData={data}
        orderNumber={orderNumber}
      />
      <OrderDetailsTable
        isLoading={isLoadingOrder}
        orderDate={data?.orderDate}
        orderItems={data}
        orderNumber={orderNumber}
        referenceNumber={data?.referenceNumber}
        salesOrderNumber={orderNumber}
        status={data?.orderStatus}
        onRow={handleOnRow}
      />
      <div className="cart-bottom order-detail__bottom">
        <div className="cart-bottom__action">
          <DownloadButton
            invoiceNumbers={data?.invoicesNumbers}
            orderDate={data?.orderDate}
            orderNumber={orderNumber}
            orderStatus={data?.orderStatus}
            referenceNumber={data?.referenceNumber}
            renderItem={({ onClick, loading }) => (
              <CustomButton
                disabled={isDownloadButtonDisabled}
                onClick={onClick}
                loading={loading}
              >
                {t('downloadInvoice')}
              </CustomButton>
            )}
          />
        </div>
      </div>
      <CustomButton
        className="back-btn"
        onClick={() => {
          navigate({
            pathname: linkGenerator('/orders'),
            search: `?page=${currentPage || 1}`,
          })
        }}
      >
        <ChevronLeftIcon />
        {t('searchResultsPage.button.backToOverview')}
      </CustomButton>
    </div>
  )
}

export default OrderDetails
